"use client";

import { cn } from "@garther/ui/libs";
import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";

import { Button } from "../Button";

interface InfoCardAction {
  icon: LucideIcon;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  variant?: "default" | "danger";
  disabled?: boolean;
}

interface InfoCardProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  description?: string;
  footer?: ReactNode;
  action?: InfoCardAction;
  onClick?: () => void;
  className?: string;
}

export function InfoCard({
  icon: Icon,
  title,
  subtitle,
  description,
  footer,
  action,
  onClick,
  className
}: InfoCardProps) {
  const isClickable = !!onClick;

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-3 rounded-md px-5 py-4 border border-border bg-background-secondary transition-all duration-200",
        isClickable && "hover:shadow-md hover:border-border-dark cursor-pointer",
        className
      )}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 rounded-lg bg-background-highlight group-hover:bg-background-highlight/70 transition-colors duration-200">
          <Icon className="h-5 w-5 text-text-secondary group-hover:text-text-primary transition-colors duration-200" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-responsive-xs font-medium text-text-secondary uppercase tracking-wide mb-1">
            {title}
          </p>
          <p className="text-responsive-base font-medium text-text-primary truncate mb-1">{subtitle}</p>
          {description && (
            <p className="text-responsive-sm font-normal text-text-secondary line-clamp-2 leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {action && (
          <Button
            variant="ghost"
            padding="p-2"
            className={cn(
              "flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-md",
              action.variant === "danger"
                ? "text-text-secondary hover:text-error-500 hover:bg-error-50"
                : "text-text-secondary hover:text-text-primary hover:bg-background-highlight"
            )}
            onClick={action.onClick}
            disabled={action.disabled}
            aria-label={action.label}
          >
            <action.icon className="h-4 w-4" />
          </Button>
        )}
      </div>

      {footer && (
        <div className="pt-2 border-t border-border-light">
          <div className="text-responsive-xs text-text-secondary">{footer}</div>
        </div>
      )}
    </div>
  );
}
